import '@elfalem/leaflet-curve';
import L from 'leaflet';
import 'leaflet-arrowheads';
import { MapController } from 'src/app/components/LeafletMapComponent/controllers/MapController';
import {
  CustomMarker,
  customMarkers,
  InterestLocation,
  markerGenerator,
  OverLappedInterests,
  submarkerClassNameGenerator,
} from 'src/app/components/LeafletMapComponent/leaflet.config';
import { createCurvePoint } from 'src/app/helpers/coordinates.helper';
import { getInterestsPreviousLocation } from 'src/app/services/surfer-service/surfer.service';

export class MapMarkerController extends MapController {
  /*
    An object that contains all necessary information about the interests,
    their labels, their submarkers, and their polylines, we keep all these
    information in the interestLocations object, not an array because of performance issues.
  */
  interestLocations: Record<string, InterestLocation> = {};

  /*
    We create feature groups for each type of marker (interest, submarker, polyline)
    and add them to the map. We use feature groups because they are more efficient
    for managing multiple markers than adding them to the map one by one.
  */
  subMarkersFeatureGroup: Record<string, L.FeatureGroup<any>> = {};

  curves = new Map<string, L.Curve>();

  // polyLinesFeatureGroup: Record<string, L.Polyline<any>> = {};

  // polyLinesFeatureGroupTwo: Record<string, L.Path> = {};

  primeMarkersFeatureGroup = L.featureGroup();

  allMarkers: Record<string, CustomMarker[]> = {};

  svgArrowMarker: SVGSVGElement | null = null;

  svgArrowMarkerList = new Map<string, SVGSVGElement>();

  /*
    If two markers are in the same location, we add them into overLappedMarkers.
  */
  overLappedMarkers: CustomMarker[] = [];

  setOverLappedMarkersAction: React.Dispatch<React.SetStateAction<OverLappedInterests | undefined>> | undefined;

  setSelectedInterestAction: React.Dispatch<React.SetStateAction<CustomMarker | undefined>> | undefined;

  /*
    We only want one instance of MapMarkerController during the process, we dont want many of them working on the
    background independetly.
  */
  static instance: MapMarkerController | null = null;

  static getInstance(map: L.Map): MapMarkerController {
    if (this.instance === null) {
      this.instance = new MapMarkerController(map);
    }
    return this.instance;
  }

  onConstruct({
    map,
    setOverLappedMarkers,
    setSelectedInterest,
  }: {
    map: L.Map;
    setOverLappedMarkers: React.Dispatch<React.SetStateAction<OverLappedInterests | undefined>>;
    setSelectedInterest: React.Dispatch<React.SetStateAction<CustomMarker | undefined>>;
  }) {
    this.map = map;
    map.addLayer(this.primeMarkersFeatureGroup);
    this.setSelectedInterestAction = setSelectedInterest;
    this.setOverLappedMarkersAction = setOverLappedMarkers;
  }

  /*
    Here we call the setter for interest locations, and give "onClick" effect with bindMarkersLeftClick function.
  */
  generateFunctionality({ interestLocations }: { interestLocations: Record<string, InterestLocation> }) {
    this.getInterestLocations(interestLocations);
    this.bindMarkersLeftClick();
  }

  /*
    We get interest locations from the component by calling the function Below.
  */
  getInterestLocations(interestLocations: Record<string, InterestLocation>) {
    this.interestLocations = interestLocations;
  }

  /*
    Prime markers are inital markers when user filters an NSearch search,
    all the interests with locations that found with the search, are added to the primeMarkers.
  */
  getPrimeMarkerFeatureGroup(primeMarkers: CustomMarker[]) {
    this.primeMarkersFeatureGroup = L.featureGroup(primeMarkers.map((marker) => marker.marker));
    // this.allMarkers.push(...primeMarkers);
    primeMarkers.forEach((pm) => {
      this.allMarkers[pm.id] = [{ ...pm }];
    });
    this.map.addLayer(this.primeMarkersFeatureGroup);
    // primeMarkers.map((obj) => this.getSubLocationsOfInterest(obj.id));
  }

  /*
    A reset function that works, when component unmounts, so this class does not cause any memory leaks.
  */
  resetMapMarkerController() {
    this.allMarkers = {};
    this.map.removeLayer(this.primeMarkersFeatureGroup);
    const allSubMarkersFeatureGroup = Object.values(this.subMarkersFeatureGroup);
    allSubMarkersFeatureGroup.forEach((featureGroup) => this.map.removeLayer(featureGroup));
    this.subMarkersFeatureGroup = {};
    // const allPolyLinesFeatureGroup = Object.values(this.polyLinesFeatureGroup);
    // allPolyLinesFeatureGroup.forEach((featureGroup) => this.map.removeLayer(featureGroup));
    // this.polyLinesFeatureGroup = {};
    this.interestLocations = {};
    this.overLappedMarkers = [];
    if (this.setOverLappedMarkersAction) {
      this.setOverLappedMarkersAction(undefined);
    }
    if (this.setSelectedInterestAction) {
      this.setSelectedInterestAction(undefined);
    }
    this.map.removeLayer(this.primeMarkersFeatureGroup);
    this.primeMarkersFeatureGroup = L.featureGroup([]);
    // if (this.curveLayer) this.map.removeLayer(this.curveLayer);
  }

  /*
    We give onClick effect to the primeMarkers.
  */
  bindMarkersLeftClick() {
    this.primeMarkersFeatureGroup.off('click');
    this.primeMarkersFeatureGroup.addEventListener('click', async (event: L.LeafletMouseEvent) => {
      const marker = event.propagatedFrom as L.Marker;
      // @ts-ignore
      const id = marker.options?.id;
      const overLapped = this.isUnitOverlap(id);
      console.log('overLapped', overLapped);
      if (this.setOverLappedMarkersAction) {
        this.setOverLappedMarkersAction(overLapped);
      }
      if (overLapped.overlappedArray.length === 1) {
        this.getSubLocationsOfInterest(id);
      }
    });
  }

  /*
    The function below helps us the understand that if one or more markers are overlapped,
    if so we return overLappedArray.
  */
  isUnitOverlap = (unitId: number) => {
    const overlappedArray: CustomMarker[] = [];
    const clickedUnitMarkerRect = document.getElementsByClassName(`${unitId}`)[0]?.getBoundingClientRect();
    Object.values(this.allMarkers).forEach((o) => {
      o.forEach((obj) => {
        const unitRect = document.getElementsByClassName(`${obj.id}`)[0]?.getBoundingClientRect();
        if (unitRect) {
          if (
            Math.abs(clickedUnitMarkerRect.left - unitRect.left) <= 12 &&
            Math.abs(clickedUnitMarkerRect.top - unitRect.top) <= 12
          ) {
            overlappedArray.push(obj);
          }
        }
      });
    });
    return {
      overlappedArray,
      x: clickedUnitMarkerRect.x,
      y: clickedUnitMarkerRect.y,
    };
  };

  catchOverlappedMarkers(event: L.LeafletMouseEvent) {
    const marker = event.propagatedFrom as L.Marker;
    // @ts-ignore
    const id = marker.options?.id;
    const overLapped = this.isUnitOverlap(id);
    if (this.setOverLappedMarkersAction) {
      this.setOverLappedMarkersAction(overLapped);
    }
  }

  tooglePrevMarkersOfSelectedInterest(id: string) {
    this.getSubLocationsOfInterest(id);
    if (this.setOverLappedMarkersAction) {
      this.setOverLappedMarkersAction(undefined);
    }
  }

  // when click the filter button will delete all lines and arrowhead.
  deletePrevMarkersOfFilterInterest() {
    for (const [key, value] of this.svgArrowMarkerList) {
      const getArrowMarker = this.svgArrowMarker?.querySelector(`g[id="${key}"]`);
      if (getArrowMarker) {
        getArrowMarker.remove();
      }
    }
    for (const [curveKey, curveValue] of this.curves) {
      this.map.removeLayer(curveValue);
    }
    this.curves.clear();
  }

  /*
    The function below gives us the sublocations of interest when we click on a prime marker.
    It also handles the case when the sublocations of interest are already shown or hidden.
  */
  async getSubLocationsOfInterest(id: string) {
    // Frist we get the markerToUpdate,
    const markerToUpdate = this.interestLocations[id];

    // We search for submarkers and polylines of that spesific interest
    const subMarkersOfInterest = this.subMarkersFeatureGroup[id];
    // const polyLinesOfInterest = this.polyLinesFeatureGroup[id];

    const { map } = this;
    // The function below helps us to understand that a prime marker is clicked, or a sub marker is clicked.
    if (this.setSelectedInterestAction) {
      if (!markerToUpdate) {
        let subMarkerOfInterest;
        Object.values(this.allMarkers).forEach((p) => {
          p.forEach((m) => {
            if (m.id === id) {
              subMarkerOfInterest = m;
            }
          });
        });

        if (subMarkerOfInterest) {
          this.setSelectedInterestAction(subMarkerOfInterest);
        }
      } else {
        this.setSelectedInterestAction(markerToUpdate);
      }
    }

    // If there are already sub markers of interest, we hide or show them on the map
    if (subMarkersOfInterest) {
      if (!markerToUpdate.viewSubs) {
        markerToUpdate.viewSubs = true;
        map.addLayer(subMarkersOfInterest);
        // map.addLayer(polyLinesOfInterest);
      } else {
        markerToUpdate.viewSubs = false;
        map.removeLayer(subMarkersOfInterest);
        // map.removeLayer(polyLinesOfInterest);
        const subMarkerObject: any = {};
        Object.entries(this.subMarkersFeatureGroup)
          .filter(([key, value]) => key !== markerToUpdate.id)
          .map((item) => {
            // eslint-disable-next-line prefer-destructuring
            subMarkerObject[item[0] as keyof string] = item[1];
            return subMarkerObject;
          });
        this.subMarkersFeatureGroup = subMarkerObject;
        this.allMarkers[id] = this.allMarkers[id].filter((m) => m.prime);

        // this.allMarkers = this.allMarkers.filter((m) => m.prime);
        if (this.svgArrowMarker) {
          this.svgArrowMarker.querySelector(`g[id="${markerToUpdate.id}"]`)?.remove();
        }
        const deletedCurve = this.curves.get(markerToUpdate.id);
        if (deletedCurve) map.removeLayer(deletedCurve);
        this.curves.delete(markerToUpdate.id);
      }
    } else {
      /*
        If there are no sub markers of interest, we fetch them from the API,
        we add events to them, and add them to the map.
      */
      try {
        const { data } = await getInterestsPreviousLocation(id);
        const previousLocations = data.slice(1);
        const subMarkerFeatureGroup: L.Marker<any>[] = [];
        const polyLineCoordinates: any[] = [];
        previousLocations
          .slice()
          .sort((a, b) => a.time - b.time)
          .forEach((prevLoc, _) => {
            const idAndClassName = submarkerClassNameGenerator(markerToUpdate.id, _);
            const subMarker = markerGenerator(
              [prevLoc.latitude, prevLoc.longitude],
              markerToUpdate.label || '',
              map,
              customMarkers.previousLocations(idAndClassName),
              idAndClassName,
            );
            polyLineCoordinates.push([prevLoc.latitude, prevLoc.longitude]);
            subMarkerFeatureGroup.push(subMarker);
            if (!this.allMarkers[markerToUpdate.id].find((m) => m.id === idAndClassName)) {
              this.allMarkers[markerToUpdate.id].push({
                id: idAndClassName,
                marker: subMarker,
                time: prevLoc.time,
                label: markerToUpdate.label,
                coordinates: [prevLoc.latitude, prevLoc.longitude],
                blockID: prevLoc.blockID,
              });
            }

            // if (!this.allMarkers.find((m) => m.id === idAndClassName)) {
            //   this.allMarkers.push({
            //     id: idAndClassName,
            //     marker: subMarker,
            //     time: prevLoc.time,
            //     label: markerToUpdate.label,
            //     coordinates: [prevLoc.latitude, prevLoc.longitude],
            //     blockID: prevLoc.blockID,
            //   });
            // }
          });
        polyLineCoordinates.push(markerToUpdate.coordinates);
        this.subMarkersFeatureGroup[markerToUpdate.id] = L.featureGroup(subMarkerFeatureGroup).on(
          'click',
          (e: L.LeafletMouseEvent) => {
            if (this.setSelectedInterestAction) {
              const marker = e.propagatedFrom as L.Marker;
              // @ts-ignore
              const markerId = marker.options?.id;
              // const subMarker = this.allMarkers.find((m) => m.id === markerId);
              const subMarker = this.allMarkers[markerToUpdate.id].find((m) => m.id === markerId);
              this.setSelectedInterestAction(subMarker);
            }
            this.catchOverlappedMarkers(e);
          },
        );

        const helperFunc = (coordinates: number[]) => {
          const test = new Map<string, number[]>();
          const deneme: any = [];
          coordinates.forEach((arr, i) => {
            const key = JSON.stringify(arr);
            if (!test.has(key)) {
              test.set(key, []);
              deneme.push(coordinates[i]);
            }
            test.get(key)!.push(i);
          });

          const duplicates = Array.from(test.values()).filter((group) => group.length > 1);
          return duplicates;
        };

        const sameCordinateIndexes = helperFunc(polyLineCoordinates);

        let curveList: any = [];
        if (sameCordinateIndexes.length > 0) {
          curveList = ['M', polyLineCoordinates[0]];

          for (let i = 0; i < polyLineCoordinates.length - 1; i += 1) {
            const curvePoints = createCurvePoint(polyLineCoordinates[i], polyLineCoordinates[i + 1], 0.1);
            curveList.push('Q', curvePoints, polyLineCoordinates[i + 1]);
          }
        } else {
          curveList = ['M', ...polyLineCoordinates];
        }

        this.curveLayer = L.curve(curveList, {
          color: markerToUpdate.color,
          // fill: true,
          weight: 5,
        });

        this.curves.set(`${markerToUpdate.id}`, this.curveLayer);

        this.curveLayer.addTo(map);

        // Fonksiyon verilen curve'ın SVG path'i boyunca belirli aralıklarla arrow(yön oku) çiziyor
        const addArrowMarker = () => {
          // Leaflet.curve tarafından oluşturulan gerçek SVG path elemanını alıyoruz..
          let markers: any;
          let pathEl: any;
          for (const [key, value] of this.curves) {
            const findMarker = this.svgArrowMarkerList.has(markerToUpdate.id);
            if (findMarker) {
              markers = this.svgArrowMarkerList.get(markerToUpdate.id);
              // eslint-disable-next-line no-underscore-dangle
              pathEl = (this.curves.get(markerToUpdate.id) as any)._path as SVGPathElement;
            } else {
              // eslint-disable-next-line no-underscore-dangle
              pathEl = (this.curveLayer as any)._path as SVGPathElement;
            }

            if (!pathEl) return;

            // Path'in ait olduğu SVG elemntini buluyoruz, Leaflet tüm vector layer'ları bu SVG içine render eder.
            if (markers) {
              this.svgArrowMarker = markers;
            } else {
              this.svgArrowMarker = pathEl.ownerSVGElement;
            }

            if (!this.svgArrowMarker) return;

            // Yeni katman oluşturuyor, svg içine grup oluşturuyoruz..
            const arrowLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            arrowLayer.setAttribute('id', `${markerToUpdate.id}`);

            // SVG path'in toplam uzunluğu (pixel cinsinden)
            const totalLength = pathEl.getTotalLength();
            // İlk oku 50px sonra başlatıp, her 100px'de bir yeni ok çiziyoruz..
            for (let d = 50; d < totalLength; d += 100) {
              // Okun yönünü hesaplayabilmek için birbirine yakın iki nokta alıyoruz,
              const p1 = pathEl.getPointAtLength(d - 5); // biraz gerideki nokta
              const p2 = pathEl.getPointAtLength(d); // şu anki nokta

              const angle = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI; // İki nokta arasındaki açıyı hasaplıyoruz.
              const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'path'); // SVG ok oluşturuyoruz..

              arrow.setAttribute('d', 'M -12 -10 L 0 0 L -12 10'); // Basit V şeklinde bir ok çiziyoruz..
              arrow.setAttribute('fill', 'black');
              // arrow.setAttribute('stroke', 'red'); // çizgi rengi
              arrow.setAttribute('strokeWidth', '2'); // çizgi kalınlığı

              arrow.setAttribute('transform', `translate(${p2.x}, ${p2.y}) rotate(${angle})`); // Önce p2 noktasına taşı, sonra hesaplanan açı kadar döndür..
              arrowLayer.appendChild(arrow); // Oku grup içine ekle
            }

            if (markers) {
              const existingChild = this.svgArrowMarker.querySelector(`g[id="${markerToUpdate.id}"]`);
              // Eski arrow katmanlarını siliyoruz, böylece zoom sonrası üst üste ok birikmez..
              if (existingChild) {
                existingChild.remove();
              }
              this.svgArrowMarker?.appendChild(arrowLayer); // Tüm okları SVG içine ekle
            } else {
              this.svgArrowMarker?.appendChild(arrowLayer); // Tüm okları SVG içine ekle
            }

            this.svgArrowMarkerList.set(markerToUpdate.id, this.svgArrowMarker);
          }
        };

        // Leaflet önce kendi SVG güncellenmesini bitirsin, sonra arrowları yeniden hesaplıyoruz..
        const handler = () => {
          requestAnimationFrame(() => {
            addArrowMarker();
          });
        };

        map.on('zoomend', () => handler()); // zoom editlendiğinde çalıştır
        map.on('moveend', () => handler()); // harita sürüklendiğinde çalıştır

        handler(); // İlk açılışta bir kez çiz

        map.off('zoomend', () => handler()); // component unmount olunca eventleri temizle
        map.off('moveend', () => handler());

        // arrow.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');

        // this.polyLinesFeatureGroup[markerToUpdate.id].arrowheads({
        //   size: '12px',
        //   frequency: '20px',
        //   fill: true,
        //   offsets: {
        //     start: '20px',
        //     end: '20px',
        //   },
        // });
        map.addLayer(this.subMarkersFeatureGroup[markerToUpdate.id]);
        // map.addLayer(this.polyLinesFeatureGroup[markerToUpdate.id]);
        markerToUpdate.viewSubs = true;
        // curveList = [];
      } catch (error) {
        console.log('error', error);
      }
    }
    this.interestLocations[id] = markerToUpdate;
  }
}
